/**
 * RFQ HANDOFF — the commercial bridge, and the most easily misread screen in
 * OMNΛS.
 *
 * The chain it sits in:
 *
 *   HUMAN SHORTLIST → PREPARE RFQ → HUMAN RELEASE AUTHORIZATION → the EXISTING
 *   bid package workflow (quotes, leveling, award)
 *
 * Two rules govern every word on this panel.
 *
 * 1. NOTHING HERE SENDS ANYTHING. The backend has no external send path at
 *    all. Preparing writes an internal record; authorizing records that a
 *    human permitted this provider to enter the RFQ workflow. No email, no
 *    notification, no invitation, no subcontract, no purchase order, no
 *    payment, no award. Every response repeats that in four booleans and this
 *    panel renders those booleans rather than asserting anything about them —
 *    so if the backend ever gains a real send path, the truth changes here
 *    without a code change.
 *
 * 2. THREE DIFFERENT TRUTHS ARE KEPT APART.
 *      state       what a human authorized internally
 *      shortlist   whether the decision it rested on still holds
 *      downstream  whether THIS provider actually responded
 *    `downstream` is derived per bidder key, so another provider quoting the
 *    same package can never make this one look like it answered. Deriving any
 *    of it in React would reintroduce exactly the ambiguity the backend built
 *    this table to remove.
 */
import { useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { SectionEmpty } from "../../components/workflow/WorkflowStates";
import {
  presentRelease,
  sortReleases,
  summarizeReleases,
  presentSendTruth,
} from "./exchangePresentation";
import {
  Send,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  Info,
  ExternalLink,
  MailX,
  CheckCircle2,
} from "lucide-react";

const TONE_CLASS = {
  good: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  active: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  attention: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

/**
 * The standing external-send disclosure. It is deliberately prominent rather
 * than a tooltip: a PM who believes an RFQ went out will stop chasing it.
 */
export const SendTruthNotice = ({ truth }) => {
  if (!truth) return null;
  const Icon = truth.sent ? Send : MailX;
  return (
    <div
      className={`rounded-lg border p-3 space-y-1 ${
        truth.sent ? "border-blue-500/30 bg-blue-500/5" : "border-border bg-muted/20"
      }`}
    >
      <p className="text-xs font-medium flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        {truth.headline}
      </p>
      <p className="text-[11px] text-muted-foreground">{truth.providerAware}</p>
      {/* Backend-authored operator copy, rendered verbatim. Truncation notes
          are excluded here because the panel surfaces those separately, and
          printing them in both places reads as two different problems. */}
      {truth.notes
        .filter((note) => !note.includes("was truncated at"))
        .map((note, i) => (
          <p key={i} className="text-[11px] text-muted-foreground">
            {note}
          </p>
        ))}
    </div>
  );
};

const ReleaseRow = ({
  row, canAuthorize, capabilitiesEstablished, onAuthorize, onOpenPackage, authorizing,
}) => (
  <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        {row.titleResolved ? (
          <p className="text-sm font-medium truncate">{row.title}</p>
        ) : (
          <p className="text-sm font-medium">{row.recordLabel}</p>
        )}
        <p className="text-[11px] text-muted-foreground">
          {/* The bidder key IS the commercial identity, and it is the join to
              the quote lineage — worth showing, because a PM chasing a quote
              that landed under another record needs to see it. */}
          <span className="font-mono break-all">{row.bidderKey}</span>
          {row.preparedAt ? ` · prepared ${row.preparedAt}` : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        <Badge
          variant="outline"
          className={`${TONE_CLASS[row.tone] || TONE_CLASS.unknown} font-medium text-[10px]`}
        >
          {row.label}
        </Badge>
        {/* Whether they answered is a SEPARATE fact from whether they were
            released, and it is specific to this provider. */}
        {row.downstreamLabel && row.state === "RELEASED" && (
          <Badge
            variant="outline"
            className={`${TONE_CLASS[row.downstreamTone] || TONE_CLASS.unknown} font-normal text-[10px]`}
            title="Read from this provider's own quote records at request time. Another provider's quote on this package never changes it."
          >
            {row.downstreamLabel}
          </Badge>
        )}
      </div>
    </div>

    {row.stateDetail && <p className="text-[11px] text-muted-foreground">{row.stateDetail}</p>}

    {/* Identity quality — an unconfirmed identity means dedupe is not guaranteed */}
    {!row.identityConfirmed && (
      <p className="text-[11px] text-amber-400 flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        No confirmed identity link names this record, so OMNΛS cannot guarantee this firm is
        not already quoting on this package under a different record.
      </p>
    )}
    {row.tiebreak && (
      <p className="text-[11px] text-muted-foreground flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        Several confirmed records could be this firm; OMNΛS used a stable but arbitrary choice.
        Confirm the identity links if this is the wrong one.
      </p>
    )}

    {/* Drift — the human decision under this release has since changed */}
    {row.drifted && (
      <p className="text-[11px] text-amber-400 flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        {row.driftNote ||
          `The shortlist decision has changed since this was prepared${
            row.currentShortlistState ? ` (now ${row.currentShortlistState})` : ""
          }.`}
      </p>
    )}

    {row.siblingMatch && (
      <p className="text-[11px] text-muted-foreground">
        A quote for this firm was recorded under its other company record.
      </p>
    )}

    {/* When it was released is a fact in its own right — it must not depend
        on whether someone also typed an optional note. */}
    {row.state === "RELEASED" && row.releasedAt && (
      <div className="rounded bg-background/40 border border-border/50 px-2 py-1.5">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Released {row.releasedAt}
        </p>
        {row.releaseNote && <p className="text-xs">{row.releaseNote}</p>}
      </div>
    )}

    <div className="flex flex-wrap items-center gap-2">
      {row.state === "PREPARED" && canAuthorize && (
        <Button
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => onAuthorize(row)}
          disabled={authorizing}
        >
          <ShieldCheck className="w-3.5 h-3.5 mr-1" />
          Authorize RFQ release
        </Button>
      )}
      {row.state === "PREPARED" && capabilitiesEstablished && !canAuthorize && (
        <span className="text-[10px] text-muted-foreground">
          Release authorization is reserved for project managers and admins.
        </span>
      )}
      {row.state === "RELEASED" && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={onOpenPackage}
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1" />
          Open bid package
        </Button>
      )}
    </div>
  </div>
);

const RfqHandoffPanel = ({
  releases,
  candidatesByOrganization,
  canAuthorize,
  capabilitiesEstablished,
  authorizing,
  onAuthorize,
  onOpenPackage,
}) => {
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");

  const rows = sortReleases(
    (releases?.releases || []).map((release) => presentRelease(release, candidatesByOrganization))
  );
  const summary = summarizeReleases(releases);
  const truth = presentSendTruth(releases);

  // A note belongs to ONE authorization.
  const close = () => {
    setPending(null);
    setNote("");
  };

  const submit = async () => {
    if (!pending) return;
    // `confirmed: true` is passed explicitly from this deliberate act, never
    // defaulted in the client — that is the entire point of the field.
    const ok = await onAuthorize(pending, { confirmed: true, note: note.trim() || undefined });
    if (ok) close();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Send className="w-4 h-4 text-primary" />
            RFQ handoff
            {summary.count > 0 && (
              <span className="text-muted-foreground font-normal">({summary.count})</span>
            )}
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Preparing and releasing are internal records. Quotes, leveling and the award
            decision stay in the bid package workspace.
          </p>
        </div>
        {summary.prepared > 0 && (
          <Badge
            variant="outline"
            className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-medium text-[10px]"
          >
            {summary.prepared} awaiting authorization
          </Badge>
        )}
      </div>

      <SendTruthNotice truth={truth} />

      {rows.length === 0 ? (
        <SectionEmpty
          icon={Send}
          title="Nothing prepared for RFQ yet"
          hint="Shortlist a provider, then prepare them for RFQ. Preparing records an internal handoff — it does not contact anybody."
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <ReleaseRow
              key={row.releaseId}
              row={row}
              canAuthorize={canAuthorize}
              capabilitiesEstablished={capabilitiesEstablished}
              authorizing={authorizing}
              onAuthorize={setPending}
              onOpenPackage={onOpenPackage}
            />
          ))}
        </div>
      )}

      {summary.truncated && (
        <p className="text-[11px] text-muted-foreground">
          More releases exist than were read, so these counts are floors rather than totals.
        </p>
      )}
      {/* Any overlay scan the backend had to bound is named in its own words. */}
      {summary.notes
        .filter((note_) => note_.toLowerCase().includes("truncat"))
        .map((note_, i) => (
          <p key={i} className="text-[11px] text-muted-foreground flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
            {note_}
          </p>
        ))}

      {/* Release authorization — a deliberate human act, spelled out */}
      <Dialog open={!!pending} onOpenChange={(open) => !open && !authorizing && close()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Authorize RFQ release
              {pending?.titleResolved ? ` — ${pending.title}` : ""}
            </DialogTitle>
            <DialogDescription>
              Recording that an authorized person cleared this provider to enter the OMNΛS RFQ
              workflow for this package.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1">
              <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                This does
              </p>
              <p className="text-xs text-muted-foreground">
                Record that an authorized human approved this provider and package to enter the
                OMNΛS RFQ workflow. The decision is attributed to you and dated.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
              <p className="text-xs font-semibold flex items-center gap-1.5">
                <MailX className="w-3.5 h-3.5" />
                This does not
              </p>
              <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-4">
                <li>send an email</li>
                <li>notify the provider</li>
                <li>send an external invitation</li>
                <li>issue a subcontract</li>
                <li>create a purchase order</li>
                <li>make a payment</li>
                <li>award the provider</li>
              </ul>
            </div>

            {pending?.drifted && (
              <p className="text-xs text-amber-400 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                The shortlist decision behind this release has changed since it was prepared.
                OMNΛS re-reads that decision as it authorizes and will refuse if the provider is
                no longer shortlisted.
              </p>
            )}

            {pending?.bidderKey && (
              <p className="text-[11px] text-muted-foreground">
                Commercial identity:{" "}
                <span className="font-mono break-all">{pending.bidderKey}</span>
                {pending.identityConfirmed ? " (confirmed)" : " (not confirmed)"}
              </p>
            )}

            <div>
              <Label className="text-xs" htmlFor="rfq-release-note">
                Note (optional)
              </Label>
              <Textarea
                id="rfq-release-note"
                className="mt-1"
                rows={3}
                maxLength={4000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this provider is cleared to quote"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Kept inside your company. It is never shown to the provider.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={close} disabled={authorizing}>
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={authorizing}>
                {authorizing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Authorize release
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RfqHandoffPanel;
